import {
  ComparisonOperator,
  CompoundCondition,
  CompoundFilterGroup,
  FilterMenuItem,
  LogicalOperator,
  TextOperator,
} from "./menu/types";

type MenuItemMap = Map<string, FilterMenuItem>;

function buildMenuItemMap(menuItems: FilterMenuItem[]): MenuItemMap {
  return new Map(menuItems.map((item) => [item.name, item]));
}

function buildCondition(
  fieldName: string,
  menuItem: FilterMenuItem,
  operator: string,
  value: unknown,
): CompoundCondition {
  const label = menuItem.label;

  switch (menuItem.type) {
    case "TEXT":
      return {
        fieldName,
        label,
        type: "TEXT",
        operator: operator as TextOperator,
        value: Array.isArray(value) ? value : [String(value)],
      };
    case "DATE":
      return {
        fieldName,
        label,
        type: "DATE",
        operator: operator as ComparisonOperator,
        value: String(value),
      };
    case "NUMERIC":
      return {
        fieldName,
        label,
        type: "NUMERIC",
        operator: operator as ComparisonOperator,
        value: String(value),
      };
    case "BOOLEAN":
      return {
        fieldName,
        label,
        type: "BOOLEAN",
        operator: "eq",
        value: String(value),
      };
  }
}

function parseCompoundArray(
  logic: LogicalOperator,
  conditionsArray: unknown[],
  menuItemMap: MenuItemMap,
): CompoundFilterGroup {
  const conditions: CompoundCondition[] = [];

  for (const item of conditionsArray.slice(0, 2)) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;

    const entries = Object.entries(item as Record<string, unknown>);
    if (entries.length === 0) continue;

    const [fieldName, fieldValue] = entries[0];
    const menuItem = menuItemMap.get(fieldName);
    if (!menuItem || !fieldValue || typeof fieldValue !== "object") continue;

    const [operator, value] = Object.entries(
      fieldValue as Record<string, unknown>,
    )[0];
    if (!operator || value === undefined) continue;

    conditions.push(buildCondition(fieldName, menuItem, operator, value));
  }

  return {
    id: `compound-${logic}`,
    logic,
    conditions,
  };
}

export function parseCompoundGroups(
  filterJson: Record<string, unknown>,
  menuItems: FilterMenuItem[],
): CompoundFilterGroup[] {
  const menuItemMap = buildMenuItemMap(menuItems);
  const groups: CompoundFilterGroup[] = [];

  for (const [key, value] of Object.entries(filterJson)) {
    if ((key === "and" || key === "or") && Array.isArray(value)) {
      groups.push(parseCompoundArray(key, value, menuItemMap));
    }
  }

  return groups;
}

export function getSimpleFilterJson(
  filterJson: Record<string, unknown>,
): Record<string, Record<string, unknown>> {
  const result: Record<string, Record<string, unknown>> = {};

  for (const [key, value] of Object.entries(filterJson)) {
    if (key === "and" || key === "or") continue;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = value as Record<string, unknown>;
    }
  }

  return result;
}

function serializeCondition(
  condition: CompoundCondition,
): Record<string, unknown> {
  const serializedValue =
    condition.type === "TEXT" && condition.value.length !== 1
      ? condition.value
      : condition.type === "TEXT"
        ? condition.value[0]
        : condition.value;
  return {
    [condition.fieldName]: { [condition.operator]: serializedValue },
  };
}

export function serializeCompoundGroups(
  groups: CompoundFilterGroup[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const group of groups) {
    const conditions = group.conditions.filter(Boolean).map(serializeCondition);
    result[group.logic] = conditions;
  }

  return result;
}
